import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Freeze } from 'react-freeze';
import { QueryMetaProvider } from 'react-query-swagger';
import { PatientCases } from './cases/PatientCases';
import { PatientNavigationBar } from './navigation/PatientNavigationBar';
import { PatientOverview } from './overview/PatientOverview';
import styles from './PatientProfile.module.scss';
import { PatientProfileContext, PatientTab } from './types';
import { useTabId } from '../../keep-alive/TabContext';
import { useEventCallback } from '@mui/material';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { NavigationActions } from '@lmt/unicorn-shared/app/features/navigation/reducer';

const PatientProfile: React.FC = React.memo(function PatientProfile(props) {
  const tabs = useTabs();
  const { patientId } = pages.patientProfile.useParams();
  const currentTab = pages.patientProfileWithTab.useMatch()?.params?.tabName;
  const caseId = pages.patientCases.useMatch()?.params?.caseId;

  const tabId = useTabId();
  const dispatch = useUniDispatch();
  const [isEditCanceled, cancelEditing] = useState<boolean>(false);
  const [unsavedChanges, setUnsavedChanges] = useState<{
    [key: string]: boolean;
  }>({});
  const changeUnsavedChanges = useCallback((key: string, value: boolean) => {
    setUnsavedChanges((prevValues) => {
      return { ...prevValues, [key]: value };
    });
  }, []);
  const clearUnsavedChanges = useCallback(() => {
    setUnsavedChanges({});
  }, []);
  const pageHasUnsavedChanges = useMemo(() => {
    return Object.values(unsavedChanges).includes(true);
  }, [unsavedChanges]);

  useEffect(() => {
    setTabHasUnsavedChanges(pageHasUnsavedChanges);
  }, [pageHasUnsavedChanges]);

  const setTabHasUnsavedChanges = useEventCallback((hasChanges: boolean) => {
    if (tabId) {
      dispatch(
        NavigationActions.tabHasUnsavedChangesChanged({
          id: tabId,
          hasUnsavedChanges: hasChanges,
        }),
      );
    }
  });

  const [isEdit, setEdit] = useState<boolean>(false);
  const [editableConfigId, setEditableConfigId] = useState<number>();

  const isOptionUndefined = !currentTab;
  useEffect(() => {
    if (isOptionUndefined) {
      tabs.changeTabPath(
        pages.patientProfileWithTab.link({
          patientId,
          tabName: PatientTab.Overview,
        }),
        true,
      );
    }
  }, [isOptionUndefined]);

  return (
    <PatientProfileContext.Provider
      value={{
        hasUnsavedChanges: pageHasUnsavedChanges,
        setUnsavedChanges: changeUnsavedChanges,
        clearUnsavedChanges: clearUnsavedChanges,
        isEditCanceled: isEditCanceled,
        cancelEditing: cancelEditing,
      }}
    >
      <div className={'fill-all'} data-test-id={'patient-page'}>
        <div className={styles.navigation}>
          <PatientNavigationBar
            patientId={patientId}
            currentTab={currentTab || ''}
            patientOverviewIsEdit={isEdit}
            setPatientOverviewEdit={(value: boolean) => setEdit(value)}
            editableConfigId={editableConfigId}
            setEditableConfigId={setEditableConfigId}
          />
        </div>
        <div className={styles.page}>
          <Freeze freeze={currentTab !== PatientTab.Overview}>
            <QueryMetaProvider meta={{ patientTab: PatientTab.Overview }}>
              <PatientOverview
                patientId={patientId}
                patientOverviewIsEdit={isEdit}
                setPatientOverviewEdit={(value: boolean) => setEdit(value)}
                editableConfigId={editableConfigId}
              />
            </QueryMetaProvider>
          </Freeze>
          <Freeze freeze={currentTab !== PatientTab.Cases}>
            <QueryMetaProvider meta={{ patientTab: PatientTab.Cases }}>
              <PatientCases patientId={patientId} caseId={caseId} />
            </QueryMetaProvider>
          </Freeze>
        </div>
      </div>
    </PatientProfileContext.Provider>
  );
});
export default PatientProfile;
